"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchInAppFeed,
  markFeedNotificationAsRead,
} from "@/data/v2/client/notifications/feed";

import { InAppFeedItem } from "@/data/v2/models/notifications.types";
import dayjs from "@punchup/utils/dayjs";

export const inAppFeedKeys = {
  all: ["in-app-feed"] as const,
};

export function useInAppFeed(enabled: boolean) {
  return useQuery({
    queryKey: inAppFeedKeys.all,
    queryFn: () => fetchInAppFeed(),
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useMarkFeedNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      notificationId,
      location,
    }: {
      notificationId: string;
      location?: string;
    }) => markFeedNotificationAsRead(notificationId, location),
    onMutate: async ({ notificationId }) => {
      await queryClient.cancelQueries({ queryKey: inAppFeedKeys.all });

      const previous = queryClient.getQueryData(inAppFeedKeys.all);

      queryClient.setQueryData(
        inAppFeedKeys.all,
        (old: { items: InAppFeedItem[]; hasMore: boolean } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === notificationId
                ? { ...item, is_read: true, read_at: dayjs().toISOString() }
                : item
            ),
          };
        }
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(inAppFeedKeys.all, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: inAppFeedKeys.all });
    },
  });
}
